import { animateMasthead } from './masthead-motion.js';
import { mastheadTitleScale, centeredTitleTransform } from './masthead-title.js';

// Page and chapter titles share the glyph-clipped teal treatment.
export function mountMasthead(hero, { id, caption, scene, titleRows, viewBox, centerTitle = false }) {
    const bounds = viewBox || (small => small ? '0 0 760 640' : '0 0 1200 440');
    const ns = 'http://www.w3.org/2000/svg';
    const element = (tag, attributes = {}, text = '') => {
        const node = document.createElementNS(ns, tag);
        for (const [key, value] of Object.entries(attributes)) node.setAttribute(key, value);
        if (text) node.textContent = text;
        return node;
    };
    const svg = element('svg', { viewBox: bounds(false), 'aria-hidden': 'true', focusable: 'false', class: 'research-hero-scene' });
    const defs = element('defs');
    const name = element('text', { id: `${id}-name`, class: 'research-hero-name' });
    if (centerTitle) name.setAttribute('text-anchor', 'middle');
    const lines = element('path', { id: `${id}-lines` });
    const layerPaths = (scene.layers || []).map((layer, i) => element('path', { id: `${id}-layer-${i}` }));
    const labels = element('g', { id: `${id}-labels`, class: 'research-hero-labels' });
    const labelNodes = scene.labels.map(() => element('text', { 'text-anchor': 'middle' }));
    labels.append(...labelNodes);
    const clip = element('clipPath', { id: `${id}-mask` });
    clip.append(element('use', { href: `#${id}-name` }));
    defs.append(name, lines, ...layerPaths, labels, clip);
    svg.append(defs);
    const layerUse = (layer, i, className) => {
        const group = element('g', { opacity: layer.opacity });
        const use = element('use', { href: `#${id}-layer-${i}`, class: className });
        if (layer.strokeWidth) use.style.strokeWidth = layer.strokeWidth;
        group.append(use);
        return group;
    };
    (scene.layers || []).forEach((layer, i) => svg.append(layerUse(layer, i, 'research-hero-wires')));
    svg.append(element('use', { href: `#${id}-lines`, class: 'research-hero-wires' }));
    svg.append(element('use', { href: `#${id}-labels`, class: 'research-hero-label-ink' }));
    svg.append(element('use', { href: `#${id}-name`, class: 'research-hero-type' }));
    const overlap = element('g', { 'clip-path': `url(#${id}-mask)` });
    (scene.layers || []).forEach((layer, i) => {
        if (layer.overlap !== false) overlap.append(layerUse(layer, i, 'research-hero-overlap'));
    });
    overlap.append(element('use', { href: `#${id}-lines`, class: 'research-hero-overlap' }));
    overlap.append(element('use', { href: `#${id}-labels`, class: 'research-hero-label-teal' }));
    svg.append(overlap);
    const [fallback, ...otherFallbacks] = hero.querySelectorAll('.research-hero-scene');
    if (fallback) fallback.replaceWith(svg);
    else hero.append(svg);
    otherFallbacks.forEach(node => node.remove());
    // The static shell reserves the final scene and controls before JS loads.
    // Enhance it in place so the content below never has to move down.
    const controls = hero.querySelector('.research-hero-controls') || document.createElement('div');
    controls.className = 'research-hero-controls';
    const description = controls.querySelector('span') || document.createElement('span');
    description.textContent = caption;
    controls.append(description);
    hero.append(controls);
    hero.classList.add('research-hero');

    let previousPath;
    const previousLayers = [];
    function fitTitle() {
        if (!centerTitle) return;
        const [x, y, width, height] = svg.getAttribute('viewBox').split(' ').map(Number);
        const rect = svg.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const scale = mastheadTitleScale(hero, Math.min(rect.width / width, rect.height / height), width);
        name.setAttribute('transform', centeredTitleTransform(scale, x + width / 2, y + height / 2));
    }
    const titleObserver = centerTitle ? new ResizeObserver(fitTitle) : null;
    titleObserver?.observe(svg);
    const stopMotion = animateMasthead(hero, (seconds, small, reduced) => {
        const rendered = scene.draw(seconds, small, reduced);
        if (rendered.path !== previousPath) {
            lines.setAttribute('d', rendered.path);
            previousPath = rendered.path;
        }
        layerPaths.forEach((path, i) => {
            const value = rendered.layers?.[i] || '';
            if (value !== previousLayers[i]) { path.setAttribute('d', value); previousLayers[i] = value; }
        });
        rendered.labels.forEach((label, i) => {
            labelNodes[i].setAttribute('x', label.x);
            labelNodes[i].setAttribute('y', label.y);
            labelNodes[i].textContent = label.text;
        });
    }, small => {
        svg.setAttribute('viewBox', bounds(small));
        name.replaceChildren();
        const rows = titleRows ? titleRows(small) : (small ? [[45, 265, 'M L'], [45, 440, 'Newton']] : [[50, 285, 'M L Newton']]);
        rows.forEach(([x, y, text]) => name.append(element('tspan', { x, y }, text)));
        fitTitle();
    });
    return () => { titleObserver?.disconnect(); stopMotion(); };
}
